import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, apiError, fmtDate } from "../lib/api";
import { toast } from "sonner";
import { FileBarChart, Plus, ExternalLink, Copy, Ban } from "lucide-react";

const STATUS_STYLE = {
    DRAFT: "bg-gray-100 text-gray-700 border-gray-200",
    PUBLISHED: "bg-emerald-50 text-emerald-700 border-emerald-200",
    UNPUBLISHED: "bg-amber-50 text-amber-700 border-amber-200",
};

export default function ReportsListPage() {
    const [sites, setSites] = useState([]);
    const [rows, setRows] = useState([]);
    const [loading, setLoading] = useState(true);
    const [creating, setCreating] = useState(false);
    const [form, setForm] = useState({ site_id: "", month: new Date().toISOString().slice(0, 7) });

    async function load() {
        try {
            const [s, r] = await Promise.all([api.get("/sites"), api.get("/reports")]);
            // Client-facing report portal only applies to MANAGED_CLIENT sites.
            const managed = s.data.filter((x) => (x.site_type || "MANAGED_CLIENT") === "MANAGED_CLIENT");
            setSites(managed); setRows(r.data);
            if (!form.site_id && managed.length) setForm((f) => ({ ...f, site_id: managed[0].id }));
        } catch (e) { toast.error(apiError(e)); }
        finally { setLoading(false); }
    }
    useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

    async function assemble(e) {
        e.preventDefault();
        try {
            const { data } = await api.post("/reports", form);
            toast.success("Draft assembled");
            setCreating(false);
            load();
            window.location.hash = `#/admin/reports/${data.id}`;
        } catch (e) { toast.error(apiError(e)); }
    }

    async function unpublish(id) {
        if (!window.confirm("Unpublish this report? The public link will stop working.")) return;
        try { await api.post(`/reports/${id}/unpublish`); toast.success("Unpublished"); load(); }
        catch (e) { toast.error(apiError(e)); }
    }

    return (
        <div className="mx-auto max-w-6xl px-4 sm:px-6 py-6 sm:py-10">
            <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-3">
                <div>
                    <div className="text-xs font-medium uppercase tracking-wider text-gray-500">HO Admin</div>
                    <h1 className="font-display text-2xl sm:text-3xl font-bold text-[#1E3355] mt-1 flex items-center gap-2"><FileBarChart className="w-6 h-6" /> Client reports</h1>
                    <p className="text-sm text-gray-500 mt-1">Monthly per-site reports. Nothing publishes automatically.</p>
                </div>
                <button onClick={() => setCreating(true)} className="inline-flex items-center gap-1.5 bg-[#1E3355] hover:bg-[#15243C] text-white text-sm font-semibold px-4 py-2 rounded-md" data-testid="reports-new-btn"><Plus className="w-4 h-4" /> Assemble draft</button>
            </div>

            {loading ? <div className="mt-6 h-40 rounded-xl bg-white border border-gray-100 animate-pulse" /> :
            rows.length === 0 ? <div className="mt-6 rounded-xl bg-white border border-gray-100 p-10 text-center text-sm text-gray-500" data-testid="reports-empty">No reports yet.</div> :
            <div className="mt-6 rounded-xl bg-white border border-gray-100 fx-shadow-card overflow-hidden" data-testid="reports-table">
                <table className="w-full text-sm">
                    <thead className="bg-[#F4F6F9] text-left text-[11px] font-semibold uppercase tracking-wider text-gray-500">
                        <tr><th className="px-4 py-2">Site</th><th className="px-4 py-2">Month</th><th className="px-4 py-2">Status</th><th className="px-4 py-2">Published</th><th /></tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100">
                        {rows.map((r) => (
                            <tr key={r.id} data-testid={`report-row-${r.id}`}>
                                <td className="px-4 py-3 font-semibold text-[#1E3355]">{r.site_name}</td>
                                <td className="px-4 py-3 text-gray-700">{r.month}</td>
                                <td className="px-4 py-3"><span className={`inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-semibold border ${STATUS_STYLE[r.status]}`}>{r.status}</span></td>
                                <td className="px-4 py-3 text-xs text-gray-500">{r.published_at ? `${fmtDate(r.published_at)} v${r.version}` : "—"}</td>
                                <td className="px-4 py-3 text-right space-x-2 whitespace-nowrap">
                                    <Link to={`/admin/reports/${r.id}`} className="text-[#1E3355] hover:underline text-sm font-semibold" data-testid={`report-open-${r.id}`}>Open</Link>
                                    {r.status === "PUBLISHED" && r.published_token && (
                                        <>
                                            <button onClick={() => { navigator.clipboard.writeText(`${window.location.origin}/r/${r.published_token}`); toast.success("Link copied"); }} className="text-emerald-700 text-sm inline-flex items-center gap-1" data-testid={`report-copy-${r.id}`}><Copy className="w-3 h-3" /> Copy link</button>
                                            <a href={`/r/${r.published_token}`} target="_blank" rel="noreferrer" className="text-emerald-700 text-sm inline-flex items-center gap-1" data-testid={`report-view-${r.id}`}><ExternalLink className="w-3 h-3" /> View</a>
                                            <button onClick={() => unpublish(r.id)} className="text-[#E02B2B] text-sm inline-flex items-center gap-1" data-testid={`report-unpublish-${r.id}`}><Ban className="w-3 h-3" /> Unpublish</button>
                                        </>
                                    )}
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>}

            {creating && (
                <div className="fixed inset-0 z-50 bg-black/40 backdrop-blur-sm flex items-end sm:items-center justify-center p-0 sm:p-4" data-testid="reports-create-modal">
                    <form onSubmit={assemble} className="w-full sm:max-w-md bg-white rounded-t-2xl sm:rounded-2xl shadow-2xl overflow-hidden">
                        <div className="p-5 border-b border-gray-100">
                            <div className="text-[10px] font-bold uppercase tracking-widest text-[#E02B2B]">Assemble</div>
                            <div className="font-display font-semibold text-[#1E3355] mt-0.5">New monthly report</div>
                            <div className="text-xs text-gray-500 mt-1">Pulls checklist, SR, attendance, consumables, usage and compliance for the selected month. Nothing publishes automatically.</div>
                        </div>
                        <div className="p-5 space-y-3">
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">Site</label>
                                <select value={form.site_id} onChange={(e) => setForm({ ...form, site_id: e.target.value })} className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm" data-testid="reports-form-site">
                                    {sites.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
                                </select>
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">Month</label>
                                <input type="month" value={form.month} onChange={(e) => setForm({ ...form, month: e.target.value })} className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm" data-testid="reports-form-month" required />
                            </div>
                        </div>
                        <div className="flex border-t border-gray-100">
                            <button type="button" onClick={() => setCreating(false)} className="flex-1 py-3 text-sm text-gray-600 hover:bg-gray-50">Cancel</button>
                            <button type="submit" className="flex-1 py-3 text-sm font-semibold text-white bg-[#1E3355] hover:bg-[#15243C]" data-testid="reports-form-submit">Assemble draft</button>
                        </div>
                    </form>
                </div>
            )}
        </div>
    );
}
